'use client';
import { ApolloLink, HttpLink } from '@apollo/client';
import type { NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

import {
  NextSSRApolloClient,
  NextSSRInMemoryCache,
  SSRMultipartLink,
} from '@apollo/experimental-nextjs-app-support/ssr';

import possibleTypes from '@nerdwallet/apollo/possible-types';

import { getCookie } from '@lib/cookie-helper';
import { getLogger } from '@lib/logging';

import appConfig from '@nerdwallet/app-config';

import typePolicies from './type-policies';
import evictCacheLink from './links/evict-cache';
import financialPortalAccountSync from './links/financial-portal-account-sync';

const logger = getLogger('my-nerdwallet apollo');

let apolloClient = null;

export const getApolloClient = () => apolloClient;

export function makeClient(): NextSSRApolloClient<NormalizedCacheObject> {
  const serverHttpLink = new HttpLink({
    uri: appConfig.API.BASE_URLS.SUPERGRAPH,
    credentials: 'include',
    headers: {
      'Content-type': 'application/json',
      'GraphQL-Client-Name': 'my-nerdwallet-next',
      'X-Application-ID': 'my-nerdwallet-next',
      'X-Caller-Client-ID': 'my-nerdwallet-next',
      'X-Client-Platform': 'server',
      'X-Client-Version': '0',
    },
  });
  apolloClient = new NextSSRApolloClient({
    cache: new NextSSRInMemoryCache({
      possibleTypes: possibleTypes.possibleTypes,
      typePolicies,
    }),
    connectToDevTools: ['development', 'staging'].includes(
      process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
    ),
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            onError(({ graphQLErrors, networkError }) => {
              if (graphQLErrors) {
                graphQLErrors.map(({ message, locations, path }) =>
                  // eslint-disable-next-line no-console
                  logger.log(
                    `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                  ),
                );
              }
              // eslint-disable-next-line no-console
              logger.log(`[Network Error] ${networkError}`);
            }),
            new SSRMultipartLink({
              stripDefer: true,
            }),
            serverHttpLink,
          ])
        : ApolloLink.from([
            // Make the apolloClient accessible for evictCacheLink and other links
            setContext((_, { headers }) => ({
              headers,
              client: apolloClient,
            })),
            evictCacheLink,
            financialPortalAccountSync,
            onError(({ graphQLErrors, networkError }) => {
              if (graphQLErrors) {
                graphQLErrors.map(({ message, locations, path }) =>
                  // eslint-disable-next-line no-console
                  logger.log(
                    `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                  ),
                );
              }
              // eslint-disable-next-line no-console
              logger.log(`[Network Error] ${networkError}`);
            }),
            new HttpLink({
              uri: appConfig.API.BASE_URLS.SUPERGRAPH,
              credentials: 'include',
              headers: {
                'Content-type': 'application/json',
                'GraphQL-Client-Name': 'my-nerdwallet-next',
                'X-Application-ID': 'my-nerdwallet-next',
                'X-Caller-Client-ID': 'my-nerdwallet-next',
                'X-Client-Platform': 'web',
                'X-Client-Version': '0',
                'x-originating-url': window.location.href,
                'X-Header-Csrf-Token': getCookie('csrf_token'),
              },
            }),
          ]),
  });

  return apolloClient;
}

export default makeClient;
